/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import ChevronLeft from '@accelint/icons/chevron-left';
import { useContext } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { Button } from '../button';
import { Icon } from '../icon';
import { PaginationContext } from './context';
import styles from './styles.module.css';
import { isNavigationDisabled } from './utils';
import type { PaginationPrevProps } from './types';

/**
 * PaginationPrev - Previous page navigation button
 *
 * @example
 * ```tsx
 * <Pagination page={5} total={10}>
 *   <PaginationPrev onPress={(page) => console.log('Previous page:', page)} />
 * </Pagination>
 * ```
 *
 * @param props - {@link PaginationPrevProps}
 * @param props.className - Optional CSS class name.
 * @param props.onPress - Handler called when the previous button is pressed.
 * @returns The rendered PaginationPrev component.
 */
export function PaginationPrev({ className, onPress }: PaginationPrevProps) {
  const { page, total, setPage } = useContext(PaginationContext);

  return (
    <Button
      className={composeRenderProps(className, (className) =>
        clsx(styles.button, className),
      )}
      color='mono-bold'
      variant='icon'
      isDisabled={page === 1 || isNavigationDisabled(total, page)}
      onPress={() => {
        setPage(page - 1);
        onPress?.(page - 1);
      }}
      aria-label='Previous page'
    >
      <Icon>
        <ChevronLeft />
      </Icon>
    </Button>
  );
}
